/**
 * Response Body Formatter Utility
 *
 * Formats API response bodies for display with syntax highlighting.
 * Handles JSON, XML, and text content types.
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5, 8.6, 15.4
 */

import type { FormattedResponseBody } from '../types';

/**
 * Formats a response body for display with syntax highlighting
 *
 * @param body - The response body to format (can be any type)
 * @param contentType - The Content-Type header value
 * @returns Formatted body string and language hint for syntax highlighting
 *
 * @example
 * // JSON response
 * formatResponseBody({ name: "John" }, "application/json")
 * // Returns: { formatted: '{\n  "name": "John"\n}', language: 'json' }
 *
 * @example
 * // XML response
 * formatResponseBody("<root><item>value</item></root>", "application/xml")
 * // Returns: { formatted: '<root><item>value</item></root>', language: 'xml' }
 *
 * @example
 * // Text response
 * formatResponseBody("Plain text", "text/plain")
 * // Returns: { formatted: 'Plain text', language: 'text' }
 *
 * @example
 * // Null/undefined body
 * formatResponseBody(null, "application/json")
 * // Returns: { formatted: '', language: 'text' }
 *
 * Preconditions:
 * - body is defined (may be null, string, or object)
 * - contentType is string (e.g., 'application/json', 'text/plain')
 *
 * Postconditions:
 * - Returns formatted string representation of body
 * - Returns language hint for syntax highlighting ('json', 'xml', 'text')
 * - JSON bodies are pretty-printed with 2-space indentation
 * - XML bodies are returned as-is
 * - Text bodies are returned as-is
 * - No mutations to input parameters
 */
export function formatResponseBody(body: any, contentType: string): FormattedResponseBody {
  // Handle null/undefined bodies (Requirement 8.5)
  if (body === null || body === undefined) {
    return {
      formatted: '',
      language: 'text',
    };
  }

  // Normalize content type to lowercase for comparison
  const normalizedContentType = contentType.toLowerCase();

  // Handle JSON content type (Requirements 8.1, 8.2)
  if (normalizedContentType.includes('application/json')) {
    try {
      // If body is already an object, stringify it with pretty-printing
      // If body is a string, parse and re-stringify for consistent formatting
      const jsonObject = typeof body === 'string' ? JSON.parse(body) : body;

      // Pretty-print with 2-space indentation (Requirement 8.1)
      const formatted = JSON.stringify(jsonObject, null, 2);

      return {
        formatted,
        language: 'json', // Language hint for syntax highlighting (Requirement 8.2)
      };
    } catch (error) {
      // If JSON parsing fails, return as text
      return {
        formatted: String(body),
        language: 'text',
      };
    }
  }

  // Handle XML content type (Requirement 8.3)
  if (
    normalizedContentType.includes('application/xml') ||
    normalizedContentType.includes('text/xml')
  ) {
    return {
      formatted: String(body), // Return as-is
      language: 'xml', // Language hint for syntax highlighting
    };
  }

  // Handle all other content types as text (Requirement 8.4)
  return {
    formatted: String(body), // Return as-is
    language: 'text', // Language hint for syntax highlighting
  };
}
